import React, { useState, useEffect } from 'react';
import { 
  Box, TextField, Stack, Button, MenuItem, 
  Select, FormControl, InputLabel, Grid, Chip,
  InputAdornment
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker, DatePicker } from '@mui/x-date-pickers'; // Added DatePicker
import { authenticationService } from 'src/services/authentication.service';
import { get,post } from 'src/services/httpClient.service';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import dayjs from 'dayjs';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import PageTitle from 'src/components/PageTitle';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import reportDownloadService from 'src/services/reportDownload.service';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';

const SubscriberPackagesDetails = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [downloading, setDownloading] = useState({
    pdf: false,
    excel: false
  });
  const [auditLoading, setAuditLoading] = useState(false);
  const { showSnackbar } = useSnackbar();
  
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  const handleGenerateAuditReport = async () => {
    try {
      setAuditLoading(true);

      const payload = {
        reportType: 'SUBSCRIBER_PACKAGE_DETAILS',
        vendorId,
        customerId: filters.customerId || null,
        packageId: filters.packageId && filters.packageId !== 'All' ? filters.packageId : null,
        status: filters.status && filters.status !== 'All' ? filters.status : null,
        filterDate: filters.filterDate ? dayjs(filters.filterDate).format('YYYY-MM-DD HH:mm:ss') : null // Changed from fromDate/toDate
      };

      const response = await post(
        `${process.env.REACT_APP_BASE_API_URL}/api/audit-report/generate`,
        payload
      );

      if (response?.token) {
        showSnackbar(response.token, 'success');
      }
    } catch (error) {
      console.error(error);
      showSnackbar('Failed to generate audit report', 'error');
    } finally {
      setAuditLoading(false);
    }
  };
  
  const [filters, setFilters] = useState({
    customerId: '',
    packageId: '',
    status: 'All',
    filterDate: null // Changed from fromDate/toDate to single filterDate
  });
  const [packageNames, setPackageNames] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  
  const statusOptions = ['All', 'ACTIVE', 'INACTIVE', 'EXPIRED','SUSPENDED','BLOCKLISTED'];

  useEffect(() => {
    const fetchPackageNames = async () => {
      if (!vendorId) return;
      try {
        const response = await get(
          `${process.env.REACT_APP_BASE_API_URL}/api/reports/vendor/${vendorId}/package-names`
        );
        setPackageNames(['All', ...response?.data]);
      } catch (error) {
        console.error('Error fetching package names:', error);
      }
    };
    fetchPackageNames();
  }, [vendorId]);

  const fetchData = async () => {
    if (!vendorId) {
      console.warn('Vendor ID not available');
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.customerId) params.append('customerId', filters.customerId);
      if (filters.packageId && filters.packageId !== 'All') params.append('packageId', filters.packageId);
      if (filters.status && filters.status !== 'All') params.append('status', filters.status);
      
      // Changed to single date parameter
      if (filters.filterDate) {
        const formattedDate = dayjs(filters.filterDate).format('YYYY-MM-DD HH:mm:ss');
        params.append('filterDate', formattedDate);
      }
      
      const response = await get(
        `${process.env.REACT_APP_BASE_API_URL}/api/reports/vendor/${vendorId}/subscriber-package-details?${params.toString()}`
      );
      
      const data = response?.data.map((row, index) => ({
        id: row.subscriptionId + '-' + row.packageId + '-' + index,
        customerId: row.customerId || 'N/A',
        subscriptionId: row.subscriptionId,
        userName: row.userName || (row.firstName + ' ' + row.lastName) || 'N/A',
        phoneNumber: row.phoneNumber || 'N/A',
        deviceId: row.deviceId || 'N/A',
        packageId: row.packageId,
        packageName: row.packageName || `Package ${row.packageId}`,
        packageAmount: row.packageAmount || 0,
        packageType: row.packageType || 'N/A',
        status: row.status || 'N/A',
        startDate: row.startDate ? dayjs(row.startDate).format('DD/MM/YYYY HH:mm:ss') : 'N/A',
        endDate: row.endDate ? dayjs(row.endDate).format('DD/MM/YYYY HH:mm:ss') : 'N/A',
        registrationDate: row.createdDate ? dayjs(row.createdDate).format('DD/MM/YYYY HH:mm:ss') : 'N/A',
        location: row.location,
        subscriptionPlan: row.subscriptionPlan || 'N/A'
      }));
      
      setRows(data);
      setTotalRecords(data.length);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update useEffect dependencies
  useEffect(() => {
    if (vendorId) {
      fetchData();
    }
  }, [vendorId, filters.packageId, filters.status, filters.filterDate]); // Changed from filters.fromDate/filters.toDate

  useEffect(() => {
    const timer = setTimeout(() => {
      if (vendorId) {
        fetchData();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleDownloadExcel = () => {
    try {
      const excelData = filteredRows.map((row) => ({
        'Customer ID': row.customerId,
        'Subscription ID': row.subscriptionId,
        'User Name': row.userName,
        'Phone Number': row.phoneNumber,
        'Device ID': row.deviceId,
        'Package ID': row.packageId,
        'Package Name': row.packageName,
        'Package Amount': row.packageAmount,
        'Package Type': row.packageType,
        'Status': row.status,
        'Start Date': row.startDate,
        'End Date': row.endDate,
        'Registration Date': row.registrationDate,
        'Location': row.location,
        'Subscription Plan': row.subscriptionPlan
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Subscriber Packages Report');

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const fileName = `Subscriber_Packages_Report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Excel download failed:', error);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading((prev) => ({ ...prev, pdf: true }));

    try {
      await reportDownloadService.downloadReport(
        {
          title: 'Subscriber Packages Report',
          columns,
          rows: filteredRows,
          fileName: `Subscriber_Packages_Report_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`,
          excelHeaders: [
            'Customer ID',
            'Subscription ID',
            'User Name',
            'Phone Number',
            'Device ID',
            'Package Name',
            'Amount',
            'Package Type',
            'Status',
            'Start Date',
            'End Date',
            'Registration Date',
            'Location'
          ],
          excelRowMapper: (row: any) => [
            row.customerId,
            row.subscriptionId,
            row.userName,
            row.phoneNumber,
            row.deviceId,
            row.packageName,
            row.packageAmount,
            row.packageType,
            row.status,
            row.startDate,
            row.endDate,
            row.registrationDate,
            row.location
          ]
        },
        'pdf'
      );
    } catch (error) {
      console.error('PDF download failed:', error);
    } finally {
      setDownloading((prev) => ({ ...prev, pdf: false }));
    }
  };

  const columns = [
    {
      field: 'customerId',
      headerName: 'Customer ID',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <span style={{ fontWeight: 'bold' }}>{params.value}</span>
      )
    },
    {
      field: 'subscriptionId',
      headerName: 'Subscription ID',
      flex: 0.8,
      minWidth: 120
    },
    {
      field: 'userName',
      headerName: 'User Name',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'deviceId',
      headerName: 'Device ID',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'packageId',
      headerName: 'Package ID',
      flex: 0.8,
      minWidth: 100
    },
    {
      field: 'packageName',
      headerName: 'Package Name',
      flex: 1.2,
      minWidth: 200
    },
    {
      field: 'packageAmount',
      headerName: 'Amount',
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => (
        <span>₹ {params.value?.toFixed(2) || '0.00'}</span>
      )
    },
    {
      field: 'packageType',
      headerName: 'Package Type',
      flex: 0.8,
      minWidth: 120
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => {
        const status = params.value;
        let color = 'gray';
        if (status === 'ACTIVE') color = 'green';
        else if (status === 'INACTIVE') color = 'red';
        else if (status === 'EXPIRED') color = 'orange';
        return (
          <Chip 
            label={status} 
            size="small"
            sx={{ 
              backgroundColor: color + '20',
              color: color,
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      flex: 1,
      minWidth: 180
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      flex: 1,
      minWidth: 180
    },
    {
      field: 'registrationDate',
      headerName: 'Registration Date',
      flex: 1,
      minWidth: 180
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 0.8,
      minWidth: 250
    },
    {
      field: 'subscriptionPlan',
      headerName: 'Plan',
      flex: 0.8,
      minWidth: 120
    }
  ];

  const filteredRows = rows.filter((row) =>
    Object.values(row).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      customerId: '',
      packageId: 'All',
      status: 'All',
      filterDate: null // Changed
    });
  };

  return (
    <>
      {/* Header */}
      <PageTitleWrapper>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
        >
          <PageTitle
            heading="Subscriber Packages Report"
            subHeading="View and export subscriber package activation history"
            handleClick={undefined}
          />

          {hasPermission(['OWNER']) && (
            <Box sx={{ display: 'flex', justifyContent: "center", alignItems: 'center', gap: 1 }}>
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadExcel}
                >
                  Download Excel
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleDownloadPDF}
                  disabled={downloading.pdf}
                >
                  {downloading.pdf ? 'Generating...' : 'Download PDF'}
                </Button>
              </>
            </Box>
          )}
        </Box>
      </PageTitleWrapper>

      {/* Filters Section - Updated with single date picker */}
      <Box sx={{ px: 2, pb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} alignItems="center">
            
            {/* Search */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search all fields..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) =>
                    handleFilterChange('status', e.target.value)
                  }
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Single Date Picker - Changed from DateTimePicker to DatePicker */}
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Filter Date"
                value={filters.filterDate}
                onChange={(newValue) =>
                  handleFilterChange('filterDate', newValue)
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  }
                }}
              />
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                size="small"
              >
                Clear Filters
              </Button>
            </Grid>

          </Grid>
        </LocalizationProvider>
      </Box>

      {/* Record Count */}
      <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <span style={{ fontWeight: 'bold' }}>Total Records: </span>
          <span>{totalRecords}</span>
        </Box>
        <Box>
          <span style={{ fontSize: '0.9rem', color: '#666' }}>
            Showing {filteredRows.length} of {totalRecords} records
          </span>
        </Box>
      </Box>

      {/* Data Grid */}
      <Box
        sx={{
          height: 'calc(100vh - 320px)',
          width: '100%',
          px: 2,
          pb: 2
        }}
      >
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          getRowId={(row) => row.id || row.subscriptionId + '-' + row.packageId || Math.random()}
          getRowHeight={() => 'auto'}
          density="compact"
          sx={{
            boxShadow: 'none',
            border: 2,
            borderColor: 'rgb(43 44 45)',
            '& .MuiDataGrid-cell': {
              alignItems: 'center',
              padding: '8px 12px'
            },
          }}
        />
      </Box>
    </>
  );
};

export default SubscriberPackagesDetails;